// Dashboard / app UI languages (not tenant-site languages — those live in src/lib/i18n/*).
// Edge-safe and isomorphic: no imports, usable from server, client, middleware and scripts.

export const UI_LOCALES = ["en", "fr", "ar"] as const;
export type UiLocale = (typeof UI_LOCALES)[number];
export const DEFAULT_UI_LOCALE: UiLocale = "en";

/** Cookie that remembers the chosen UI language (also for signed-out visitors). */
export const UI_LOCALE_COOKIE = "bts_ui_locale";
/** Key inside User.preferences (JSON) that stores the signed-in user's choice. */
export const UI_LOCALE_PREF_KEY = "uiLocale";

export const UI_LOCALE_META: Record<UiLocale, { name: string; native: string; dir: "ltr" | "rtl" }> = {
  en: { name: "English", native: "English", dir: "ltr" },
  fr: { name: "French", native: "Français", dir: "ltr" },
  ar: { name: "Arabic", native: "العربية", dir: "rtl" },
};

export function isUiLocale(v: unknown): v is UiLocale {
  return typeof v === "string" && (UI_LOCALES as readonly string[]).includes(v);
}

/** "fr-CA" / "FR" / "ar_MA" → "fr" / "fr" / "ar"; null when not a UI language. */
export function toUiLocale(v: string | null | undefined): UiLocale | null {
  if (!v) return null;
  const primary = v.trim().toLowerCase().split(/[-_]/)[0];
  return isUiLocale(primary) ? primary : null;
}

export function uiDir(locale: UiLocale): "ltr" | "rtl" {
  return UI_LOCALE_META[locale].dir;
}

/**
 * BCP 47 tag handed to Intl formatters. Arabic uses Latin digits (nu-latn) so numbers,
 * prices and dates read the same as the IDs, URLs and data around them and so server and
 * browser ICU builds can't disagree on the numbering system. Change it here if needed.
 */
export function intlLocale(locale: UiLocale): string {
  return locale === "ar" ? "ar-u-nu-latn" : locale === "fr" ? "fr-FR" : "en-US";
}
